import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiNavigation, FiAward } = FiIcons;

const Location = () => {
  const advantages = [
    {
      title: '1.1km to Kudawe Gate',
      description: 'Perfect proximity - close enough for dawn access, far enough for comfort',
      icon: FiMapPin,
      metric: '3-minute walk'
    },
    {
      title: 'Strategic Border Position',
      description: 'Unique access to Kudawe-Ullinduwawa ecotone for edge specialist species',
      icon: FiNavigation,
      metric: 'Exclusive access'
    },
    {
      title: 'Bird Wave Epicenter',
      description: 'World\'s largest mixed-species flocks pass directly through our trails',
      icon: FiAward,
      metric: '50+ species per wave'
    },
    {
      title: 'Early Morning Advantage',
      description: 'First to reach prime birding spots before day visitors arrive',
      icon: FiClock,
      metric: '5:00 AM starts'
    }
  ];

  const directions = [
    { from: 'Colombo Airport', duration: '3.5 hours', distance: '180km' },
    { from: 'Colombo City', duration: '4 hours', distance: '200km' },
    { from: 'Galle', duration: '2.5 hours', distance: '120km' },
    { from: 'Ratnapura', duration: '1.5 hours', distance: '60km' }
  ];

  return (
    <section id="location" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
            The Perfect Location
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Strategically positioned at the Kudawe-Ullinduwawa border for unparalleled birding access
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Map Placeholder */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="glass rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Interactive Location Map</h3>
            <div className="aspect-square bg-gradient-to-br from-emerald-600/20 to-teal-600/20 rounded-xl flex items-center justify-center mb-6">
              <div className="text-center">
                <SafeIcon icon={FiMapPin} className="text-6xl text-emerald-400 mx-auto mb-4" />
                <p className="text-white text-lg">Kudawe Gate - 1.1km</p>
                <p className="text-gray-400">Sinharaja Forest Reserve</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 glass-dark rounded-lg">
                <span className="text-white">Distance to Gate</span>
                <span className="text-emerald-400 font-semibold">1.1km</span>
              </div>
              <div className="flex justify-between items-center p-3 glass-dark rounded-lg">
                <span className="text-white">Walking Time</span>
                <span className="text-emerald-400 font-semibold">3 minutes</span>
              </div>
              <div className="flex justify-between items-center p-3 glass-dark rounded-lg">
                <span className="text-white">Elevation</span>
                <span className="text-emerald-400 font-semibold">300m ASL</span>
              </div>
            </div>
          </motion.div>

          {/* Location Advantages */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-bold text-white mb-8">Location Advantages</h3>
            <div className="space-y-6">
              {advantages.map((advantage, index) => (
                <motion.div
                  key={advantage.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass rounded-lg p-6 hover-lift group cursor-pointer"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <SafeIcon icon={advantage.icon} className="text-white text-xl" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-semibold text-lg group-hover:text-emerald-300 transition-colors">
                          {advantage.title}
                        </h4>
                        <span className="text-emerald-400 text-sm font-medium">
                          {advantage.metric}
                        </span>
                      </div>
                      <p className="text-gray-300">
                        {advantage.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Directions */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-white mb-8 text-center font-playfair">
            Getting Here
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {directions.map((direction, index) => (
              <motion.div
                key={direction.from}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-lg p-6 text-center hover-lift"
              >
                <h4 className="text-white font-semibold text-lg mb-3">{direction.from}</h4>
                <div className="space-y-2">
                  <div className="text-emerald-400 text-2xl font-bold">{direction.duration}</div>
                  <div className="text-gray-400">{direction.distance}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Competitive Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="glass rounded-2xl p-8"
        >
          <h3 className="text-3xl font-bold text-white mb-8 text-center font-playfair">
            Why Jansen's Location Wins
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-white font-semibold py-4">Lodge</th>
                  <th className="text-white font-semibold py-4">Gate Distance</th>
                  <th className="text-white font-semibold py-4">Comfort Level</th>
                  <th className="text-white font-semibold py-4">Target Guest</th>
                </tr>
              </thead>
              <tbody className="space-y-4">
                <tr className="border-b border-white/10 bg-emerald-500/10">
                  <td className="py-4 text-emerald-400 font-semibold">Jansen's Bungalow</td>
                  <td className="py-4 text-white">Excellent (1.1km)</td>
                  <td className="py-4 text-white">High (AC, Pool)</td>
                  <td className="py-4 text-white">Comfort Lister</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 text-gray-300">Martin's Lodge</td>
                  <td className="py-4 text-gray-300">At Gate (0km)</td>
                  <td className="py-4 text-gray-300">Low (Basic)</td>
                  <td className="py-4 text-gray-300">Backpacker</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 text-gray-300">Rainforest Ecolodge</td>
                  <td className="py-4 text-gray-300">Poor (Distant)</td>
                  <td className="py-4 text-gray-300">Ultra-High</td>
                  <td className="py-4 text-gray-300">Luxury Tourist</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-8 text-center">
            <p className="text-emerald-300 text-lg font-medium">
              "The Proximity of a Lodge, The Comfort of a Resort"
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Location;